import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import Home from "@/pages/Home";
import StoryDetail from "@/pages/StoryDetail";
import SeriesHub from "@/pages/SeriesHub";
import ComingSoon from "@/pages/ComingSoon";
import Admin from "@/pages/Admin";
import Login from "@/pages/Login";
import Saved from "@/pages/Saved";
import StoriesSearch from "@/pages/StoriesSearch";
import SeriesWatch from "@/pages/SeriesWatch";
import RadioPage from "@/pages/RadioPage";
import ArtPage from "@/pages/ArtPage";
import PodcastPage from "@/pages/PodcastPage";
import { InstallPrompt } from "@/components/site/InstallPrompt";
import { RadioProvider } from "@/context/RadioContext";
import { RadioBar } from "@/components/radio/RadioBar";
import { Toaster } from "@/components/ui/sonner";
import { isLive } from "@/launch";
import axios from "axios";
import { useLocation } from "react-router-dom";

axios.defaults.withCredentials = true;

function GlobalRadio() {
  const { pathname } = useLocation();
  const hidden = pathname.startsWith("/admin") || pathname.startsWith("/login") || pathname === "/coming-soon";
  if (hidden) return null;
  return <RadioBar />;
}

function RootGate() {
  const [live, setLive] = useState(() => isLive());
  if (live) return <Home />;
  return <ComingSoon onLive={() => setLive(true)} />;
}

function App() {
  useEffect(() => {
    const HEADER_OFFSET = 68;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let lenis = null;
    let raf;
    if (!reduce) {
      lenis = new Lenis({ duration: 1.15, smoothWheel: true });
      window.__lenis = lenis;
      const loop = (t) => {
        lenis.raf(t);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    }

    const onAnchorClick = (e) => {
      const link = e.target.closest('a[href^="#"]');
      if (!link) return;
      const hash = link.getAttribute("href");
      if (!hash || hash === "#") return;
      const target = hash === "#top" ? 0 : document.querySelector(hash);
      if (target === null) return;
      e.preventDefault();
      if (lenis) {
        lenis.scrollTo(target, { offset: -HEADER_OFFSET });
      } else if (target === 0) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const y = target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    };
    document.addEventListener("click", onAnchorClick);

    return () => {
      document.removeEventListener("click", onAnchorClick);
      if (raf) cancelAnimationFrame(raf);
      if (lenis) lenis.destroy();
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <RadioProvider>
          <Routes>
            <Route path="/" element={<RootGate />} />
            <Route path="/coming-soon" element={<ComingSoon />} />
            <Route path="/preview" element={<Home />} />
            <Route path="/story/:id" element={<StoryDetail />} />
            <Route path="/series" element={<SeriesHub />} />
            <Route path="/series/:slug" element={<SeriesWatch />} />
            <Route path="/saved" element={<Saved />} />
            <Route path="/stories" element={<StoriesSearch />} />
            <Route path="/radio" element={<RadioPage />} />
            <Route path="/art" element={<ArtPage />} />
            <Route path="/podcast" element={<PodcastPage />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/login" element={<Login />} />
          </Routes>
          <GlobalRadio />
          <InstallPrompt />
        </RadioProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}

export default App;
